import React, {useState} from 'react';
import {NotificationType, useNotification} from "../../../context/NotificationContext.tsx";
import {useAuthApi} from "../../../context/ApiContext.tsx";

interface PasswordValidation {
    length: boolean;
    lowercase: boolean;
    uppercase: boolean;
    number: boolean;
    special: boolean;
}

interface ChangePasswordProps {
    onSuccess?: () => void;
}

export const ChangePassword: React.FC<ChangePasswordProps> = ({ onSuccess }) => {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { showNotification } = useNotification();
    const authApi = useAuthApi();

    const validatePassword = (password: string): PasswordValidation => ({
        length: password.length >= 16,
        lowercase: /[a-z]/.test(password),
        uppercase: /[A-Z]/.test(password),
        number: /[0-9]/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });

    const [validation, setValidation] = useState<PasswordValidation>({
        length: false,
        lowercase: false,
        uppercase: false,
        number: false,
        special: false,
    });

    const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const password = e.target.value;
        setNewPassword(password);
        setValidation(validatePassword(password));
    };

    const isPasswordValid = Object.values(validation).every(Boolean);
    const doPasswordsMatch = newPassword === confirmPassword;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isPasswordValid) {
            showNotification(NotificationType.ERROR, 'Please ensure your password meets all requirements');
            return;
        }

        if (!doPasswordsMatch) {
            showNotification( NotificationType.ERROR, 'Passwords do not match');
            return;
        }

        try {
            setIsLoading(true);
            const response = await authApi.changePassword({ oldPassword, newPassword });
            showNotification(NotificationType.SUCCESS, response || 'Password changed successfully');

            // Reset form
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setValidation({
                length: false,
                lowercase: false,
                uppercase: false,
                number: false,
                special: false,
            });

            // Call onSuccess callback if provided
            onSuccess?.();
        } catch (error) {
            console.log(error);
        }
        finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label htmlFor="current-password" className="block text-sm font-medium text-slate-700">
                        Current Password
                    </label>
                    <input
                        type="password"
                        id="current-password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm
                                 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        required
                    />
                </div>

                <div>
                    <label htmlFor="new-password" className="block text-sm font-medium text-slate-700">
                        New Password
                    </label>
                    <input
                        type="password"
                        id="new-password"
                        value={newPassword}
                        onChange={handleNewPasswordChange}
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm
                                 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        required
                    />

                    <div className="mt-3 space-y-2">
                        <p className="text-sm font-medium text-slate-700">Password Requirements:</p>
                        <ul className="space-y-1 text-sm">
                            <li className={validation.length ? 'text-green-600' : 'text-slate-600'}>
                                • At least 16 characters
                            </li>
                            <li className={validation.lowercase ? 'text-green-600' : 'text-slate-600'}>
                                • One lowercase letter
                            </li>
                            <li className={validation.uppercase ? 'text-green-600' : 'text-slate-600'}>
                                • One uppercase letter
                            </li>
                            <li className={validation.number ? 'text-green-600' : 'text-slate-600'}>
                                • One number
                            </li>
                            <li className={validation.special ? 'text-green-600' : 'text-slate-600'}>
                                • One special character
                            </li>
                        </ul>
                    </div>
                </div>

                <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700">
                        Confirm New Password
                    </label>
                    <input
                        type="password"
                        id="confirm-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm
                                 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        required
                    />
                    {confirmPassword && !doPasswordsMatch && (
                        <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
                    )}
                </div>

                <button
                    type="submit"
                    disabled={!isPasswordValid || !doPasswordsMatch || isLoading}
                    className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white
                             hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500
                             focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isLoading ? 'Changing Password...' : 'Change Password'}
                </button>
            </form>
        </div>
    );
};